import { callAPI } from '../../../services/api.service';
import { ICharacterOutlineByUser, StoryCreationMethodEnum, StoryLanguageTypeEnum } from '../../../interfaces/book.interface';

export interface AutosaveData {
  email: string;
  bookId: string;
  step: number;
  storyCreationMethod?: StoryCreationMethodEnum;
  storyTopic?: string | null;
  storyType?: string | null;
  writingStyle?: string | null;
  storyLanguage?: StoryLanguageTypeEnum;
  pagesText?: { [pageName: string]: string };
  characterOutlineByUser?: ICharacterOutlineByUser;
}

/**
 * Fire-and-forget autosave using navigator.sendBeacon.
 * sendBeacon is guaranteed to be queued by the browser even when the page
 * is being unloaded (visibility hidden, beforeunload, pagehide).
 * Falls back to fetch with keepalive when sendBeacon is unavailable.
 */
export const autosaveWithBeacon = (data: AutosaveData): boolean => {
  if (!data.email || !data.bookId || (!data.step && data.step !== 0)) {
    return false;
  }

  const url = `${process.env.REACT_APP_API_URL}/api/save-story-draft`;
  const payload = JSON.stringify(data);
  const blob = new Blob([payload], { type: 'application/json' });

  if (navigator.sendBeacon) {
    const queued = navigator.sendBeacon(url, blob);
    console.log(`[autosave-beacon] sendBeacon queued: ${queued}`);
    return queued;
  }

  // Fallback: fetch with keepalive (supported on most modern browsers)
  try {
    fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'include',
    });
    console.log('[autosave-beacon] fallback fetch+keepalive dispatched');
    return true;
  } catch (error) {
    console.error('[autosave-beacon] fallback failed:', error);
    return false;
  }
};

/**
 * Save story draft automatically
 * @param data Autosave data containing all the necessary information
 * @returns Promise with response data
 */
export const autosaveStoryDraft = async (data: AutosaveData): Promise<any> => {
  try {
    // Only proceed if we have the required data
    if (!data.email || !data.bookId || (!data.step && data.step !== 0)) {
      console.warn('Autosave skipped: Missing required data (email, bookId, or step)');
      return null;
    }

    console.log('Autosaving story draft...', {
      bookId: data.bookId,
      step: data.step,
      storyCreationMethod: data.storyCreationMethod,
      hasPagesText: !!data.pagesText && Object.keys(data.pagesText).length > 0,
    });

    const response = await callAPI('api/save-story-draft', data);

    if (response?.status === 200) {
      console.log('Story draft autosaved successfully');
    } else {
      console.warn('Autosave returned non-200 status:', response?.status);
    }

    return response;
  } catch (error) {
    console.error('Error during autosave:', error);
    return null;
  }
};

/**
 * Collect pages text data from bookStoryPagesData for autosave
 * @param bookStoryPagesData The pages data object
 * @returns Object with page text data formatted for backend
 */
export const collectPagesTextForAutosave = (bookStoryPagesData: {
  [pageName: string]: { text?: string };
}): { [pageName: string]: string } => {
  const pagesText: { [pageName: string]: string } = {};

  if (bookStoryPagesData && typeof bookStoryPagesData === 'object') {
    Object.keys(bookStoryPagesData).forEach((pageName) => {
      const pageData = bookStoryPagesData[pageName];
      if (pageData?.text && pageData.text.trim().length > 0) {
        pagesText[pageName] = pageData.text.trim();
      }
    });
  }

  return pagesText;
};

/**
 * Check if autosave should be triggered based on data availability
 * @param data Autosave data to validate
 * @returns boolean indicating if autosave should proceed
 */
export const shouldAutosave = (data: AutosaveData): boolean => {
  console.log('shouldAutosave called with data:', data);

  // Must have required fields
  if (!data.email || !data.bookId || !data.step) {
    console.log('Missing required fields:', { email: !!data.email, bookId: !!data.bookId, step: !!data.step });
    return false;
  }

  // For AI-assisted mode, check if we have story details
  if (data.storyCreationMethod === StoryCreationMethodEnum.AI_ASSISTED) {
    const hasAIData = !!(data.storyTopic || data.storyType || data.writingStyle || data.storyLanguage);
    console.log('AI-assisted mode, has data:', hasAIData, {
      storyTopic: !!data.storyTopic,
      storyType: !!data.storyType,
      writingStyle: !!data.writingStyle,
      storyLanguage: !!data.storyLanguage,
    });
    return hasAIData;
  }

  // For self-written mode, check if we have pages text
  if (data.storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN) {
    const hasPagesText = !!(data.pagesText && Object.keys(data.pagesText).length > 0);
    console.log('Self-written mode, has pages text:', hasPagesText, {
      pagesText: data.pagesText,
      pageCount: data.pagesText ? Object.keys(data.pagesText).length : 0,
    });
    return hasPagesText;
  }

  // Default case - allow autosave if basic data is present
  console.log('Default case - allowing autosave');
  return true;
};
